import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { UserTransactionStatusEnum } from "../../_common/enum/userTransactionStatus.enum";
import { IFilteringViewModel } from "../../_common/interface/table";
import { IUserCartItemList } from "../../_common/interface/userCartItem/iUserCartItemList";
import StyledGrid from "../../_components/grid/styledGrid";
import { PageHeader } from "../../_components/header/page.header";
import { CommonTable } from "../../_components/table/common.table";
import { getDateTime } from "../../_helpers/dateTime.helper";
import { getMoneyFormat } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { userTransactionService } from "../../_services/userTransaction.service";
import { useContainerStyles } from "../../_styles/container.styles";
import { PrimaryButton } from "../../_components/button/primary.button";

export function UserTransactionIndex() {

    const [data, setData] = useState<any>({ items: [], count: 0 });
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState<IFilteringViewModel | null>();

    const [search, setSearch] = useState<any>({
        formStatusId: "",
        userName: "",
        formTemplateTypeId: "",
        fromDate: "",
        toDate: ""
    });

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'Id',
                size: 5
            },
            {
                accessorKey: 'userName',
                header: 'User Name',
            },
            {
                accessorKey: 'totalAmount',
                header: 'Total Amount',
                accessorFn: (rowData: any) => <>{!!rowData.totalAmount ? getMoneyFormat(rowData.totalAmount) : null}</>,
            },
            {
                accessorKey: 'userCartItems',
                header: 'Cart Items',
                Cell: ({ cell }) => (

                    <div>
                        {cell.getValue<IUserCartItemList[]>().map((item: any) => (
                            <div key={item.id}>{item.userCartItemTypeName} - {item.name} - {getMoneyFormat(item.price)}</div>
                        ))}
                    </div>
                ),
                size: 200
            },

            {
                accessorKey: 'statusCode',
                header: 'Payment Status',
                accessorFn: (rowData: any) => <>{getStatusMessage(rowData.statusCode)}</>,
            },
            {
                accessorKey: 'userTransactionCurrencyName',
                header: 'Currency',
            },
            {
                accessorKey: 'createdDate',
                header: 'Created At',
                accessorFn: (rowData: any) => <>{getDateTime(rowData.createdDate)}</>,
            }
        ],
        [],
    );

    useEffect(() => {
        if (filter)
            fetchData();
    }, [filter]);

    function fetchData() {
        return userTransactionService.getAll({ ...filter, ...search })
            .then(
                data => {
                    setData(data);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any) {
        setSearch((__filter: any) => { return { ...__filter, [event.target.name]: event.target.value } });
    };

    function getStatusMessage(statusCode: UserTransactionStatusEnum) {
        switch (statusCode) {
            case UserTransactionStatusEnum.SUCCESS:
                return "Successfully Completed";
            case UserTransactionStatusEnum.DECLINED:
                return "Authorization Declined";
            case UserTransactionStatusEnum.REDIRECTED:
                return "Redirected to the Bank Issuer";
            default:
                return "Failed";
        }
    };

    return (
        <>
            <div style={useContainerStyles.root}>

                <PageHeader pageName="User Transactions" />

                <Paper>

                    <ValidatorForm onSubmit={fetchData}>
                        <StyledGrid container spacing={2}>
                            <Grid item lg={3} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField label="User Name" name="userName" value={search.userName} onChange={handleChange} placeholder="User Name" variant="outlined" />
                                </FormControl>
                            </Grid>
                            <Grid item lg={3} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Status</InputLabel>
                                    <Select
                                        value={search.paymentStatus}
                                        name="paymentStatus"
                                        onChange={handleChange}
                                        label="Payment Status"
                                    >
                                        <MenuItem value={""}>Any Status</MenuItem>
                                        <MenuItem value={"0000"}>Completed</MenuItem>
                                        <MenuItem value={"2000"}>Declined</MenuItem>
                                        <MenuItem value={"2021"}>Redirected</MenuItem>
                                        <MenuItem value={"Failed"}>Failed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item lg={2} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        type="date"
                                        label="From Date"
                                        name="fromDate"
                                        value={search.fromDate}
                                        onChange={handleChange}
                                        placeholder="From Date"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item lg={2} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        type="date"
                                        label="To Date"
                                        name="toDate"
                                        value={search.toDate}
                                        onChange={handleChange}
                                        placeholder="To Date"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item lg={2} md={6} xs={12}>
                                <PrimaryButton
                                    fullHeight
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Search"}
                                </PrimaryButton>
                            </Grid>
                        </StyledGrid>
                    </ValidatorForm>

                    <CommonTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading}
                        setFilter={setFilter}
                        sorting={[{ desc: true, id: "id" }]}
                    />

                </Paper>

            </div >
        </>

    );

}
